/* eslint-disable react-refresh/only-export-components -- Surface predicate and the Provider-owned Host form one public lifecycle boundary. */
/** Pure surface slots; one Provider-owned physical drawer moves between them. */
import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useLayoutEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { QuoteChatWorkspaceApi } from "./QuoteChatWorkspaceContext";
import { useQuoteChatLayout } from "./useQuoteChatLayout";
import { QuoteSideDrawer } from "@/modules/quickquote/QuoteSideDrawer";
const QuoteChatSidePanel = lazy(() =>
  import("./QuoteChatSidePanel").then((module) => ({
    default: module.QuoteChatSidePanel,
  })),
);
import "./QuoteChatWorkspaceHost.css";

export type QuoteChatWorkspaceHostProps = {
  quoteId: string | null;
  owner: object;
  owns: () => boolean;
  controlsTarget: HTMLElement | null;
  railTarget: HTMLElement | null;
  inlineTarget?: HTMLElement | null;
  returnFocus: () => HTMLElement | null;
  details: ReactNode;
  askAiEligible: boolean;
};
type Host = {
  token: number;
  actor: string;
  active: boolean;
  get: () => QuoteChatWorkspaceHostProps;
};
type Registration = {
  actor: string;
  register: (owner: object, get: Host["get"]) => () => void;
  changed: () => void;
};
const Context = createContext<Registration | null>(null);
const actorKey = (workspace: QuoteChatWorkspaceApi) =>
  JSON.stringify(workspace.identity);
export function quoteChatSurfaceParked(node: HTMLElement | null): boolean {
  const origin = node?.closest<HTMLElement>(".qq-journey-origin");
  return !!origin?.hidden;
}
export function QuoteChatWorkspaceHost(props: QuoteChatWorkspaceHostProps) {
  const registration = useContext(Context),
    latest = useRef(props);
  latest.current = props;
  const lease = useRef<{
    owner: object;
    actor: string;
    cleanup: () => void;
  } | null>(null);
  const previous = useRef<QuoteChatWorkspaceHostProps | null>(null);
  useLayoutEffect(() => {
    if (!registration) return;
    const sync = () => {
      const current = latest.current;
      const active =
        current.owns() &&
        !!current.controlsTarget?.isConnected &&
        !quoteChatSurfaceParked(current.controlsTarget);
      if (
        lease.current &&
        (!active ||
          lease.current.owner !== current.owner ||
          lease.current.actor !== registration.actor)
      ) {
        lease.current.cleanup();
        lease.current = null;
      }
      if (active && !lease.current)
        lease.current = {
          owner: current.owner,
          actor: registration.actor,
          cleanup: registration.register(current.owner, () => latest.current),
        };
      else if (active && previous.current !== current) registration.changed();
      previous.current = current;
    };
    sync();
    const origin = props.controlsTarget?.closest(".qq-journey-origin");
    const observer = origin ? new MutationObserver(sync) : null;
    observer?.observe(origin!, {
      attributes: true,
      attributeFilter: ["hidden", "inert"],
    });
    return () => observer?.disconnect();
  });
  useLayoutEffect(
    () => () => {
      lease.current?.cleanup();
      lease.current = null;
    },
    [],
  );
  // Legacy standalone consumers retain their original read-only details.
  // Ask-AI controls and the chat panel still require the shared Provider.
  return registration ? null : (
    <QuoteSideDrawer
      railTarget={props.railTarget}
      inlineTarget={props.inlineTarget}
      enabled={!!props.quoteId && props.owns()}
      ownerKey={props.quoteId ?? ""}
    >
      {props.details}
    </QuoteSideDrawer>
  );
}
export function QuoteChatHostProvider({
  workspace,
  onAmbiguous,
  children,
}: {
  workspace: QuoteChatWorkspaceApi;
  onAmbiguous: (value: boolean) => void;
  children: ReactNode;
}) {
  const actor = actorKey(workspace),
    entries = useRef(new Map<object, Host>()),
    counter = useRef(0);
  const [, revise] = useState(0);
  const registration = useMemo<Registration>(
    () => ({
      actor,
      changed: () => revise((n) => n + 1),
      register: (owner, get) => {
        const host: Host = {
          token: ++counter.current,
          actor,
          active: true,
          get,
        };
        entries.current.set(owner, host);
        revise((n) => n + 1);
        return () => {
          host.active = false;
          if (entries.current.get(owner) === host) {
            entries.current.delete(owner);
            revise((n) => n + 1);
          }
        };
      },
    }),
    [actor],
  );
  const eligible = [...entries.current.values()].filter((host) => {
    const value = host.get();
    return (
      host.actor === actor &&
      host.active &&
      value.owns() &&
      !!value.controlsTarget?.isConnected &&
      !quoteChatSurfaceParked(value.controlsTarget)
    );
  });
  const host = eligible.length === 1 ? eligible[0] : null;
  useLayoutEffect(() => {
    onAmbiguous(eligible.length > 1);
  }, [eligible.length, onAmbiguous]);
  return (
    <Context.Provider value={registration}>
      {children}
      <QuoteChatPortal workspace={workspace} host={host} />
    </Context.Provider>
  );
}
function QuoteChatPortal({
  workspace,
  host,
}: {
  workspace: QuoteChatWorkspaceApi;
  host: Host | null;
}) {
  const [root] = useState(() => {
    const node = document.createElement("div");
    node.className = "qq-chat-host-root";
    return node;
  });
  const [parking] = useState(() => {
    const node = document.createElement("div");
    node.hidden = true;
    root.appendChild(node);
    return node;
  });
  const tabsId = useId();
  const lastHost = useRef<Host | null>(null);
  if (host) lastHost.current = host;
  const renderedHost = host ?? lastHost.current;
  const target = renderedHost?.get(),
    { narrow, yieldLeft } = useQuoteChatLayout();
  const mountedPanels = useRef(new Set<string>());
  const panelKey = JSON.stringify([actorKey(workspace), target?.quoteId]);
  const sameQuote =
    !!target?.quoteId && workspace.view?.quoteId === target.quoteId;
  const view = sameQuote ? workspace.view : null;
  if (view?.pane === "ask_ai") mountedPanels.current.add(panelKey);
  const [choice, setChoice] = useState<{
    token: number;
    request?: number;
    pane: "details" | "ask_ai";
  }>({ token: 0, pane: "details" });
  const pane =
    choice.token === host?.token && choice.request === view?.requestId
      ? choice.pane
      : (view?.pane ?? "details");
  const ownerKey = JSON.stringify([
    actorKey(workspace),
    host?.token,
    target?.quoteId,
  ]);
  useLayoutEffect(() => {
    if (
      host &&
      target?.controlsTarget &&
      root.parentElement !== target.controlsTarget
    )
      target.controlsTarget.appendChild(root);
    if (!host) root.remove();
  }, [root, host, target?.controlsTarget]);
  useLayoutEffect(() => () => root.remove(), [root]);
  const showingAsk =
    !!host &&
    pane === "ask_ai" &&
    !!target?.askAiEligible &&
    (!narrow || !!view?.requestedOpen);
  useLayoutEffect(() => {
    const layout = target?.controlsTarget?.closest(".wd-ws");
    if (!layout || !yieldLeft || !showingAsk) return;
    layout.classList.add("qq-chat-yield-left");
    return () => layout.classList.remove("qq-chat-yield-left");
  }, [target?.controlsTarget, yieldLeft, showingAsk]);
  const actionOwner = useMemo(
    () => ({ ownerKey, host, requestId: view?.requestId }),
    [ownerKey, host, view?.requestId],
  );
  const currentHostView = useRef({
    host,
    requestId: view?.requestId,
    showingAsk,
    actionOwner,
  });
  currentHostView.current = {
    host,
    requestId: view?.requestId,
    showingAsk,
    actionOwner,
  };
  const ownsView = () =>
    currentHostView.current.actionOwner === actionOwner &&
    currentHostView.current.host === host &&
    currentHostView.current.requestId === view?.requestId &&
    currentHostView.current.showingAsk &&
    !!host?.active &&
    host.get().owns();
  const changePane = (next: "details" | "ask_ai") => {
    if (!host) return;
    if (next === "ask_ai") {
      if (target?.quoteId) workspace.openAskAi(target.quoteId);
    } else {
      setChoice({
        token: host.token,
        request: view?.requestId,
        pane: "details",
      });
    }
  };
  const tabs = target?.askAiEligible ? (
    <div className="qq-chat-tabs" role="tablist" aria-label="报价辅助视图">
      {(["details", "ask_ai"] as const).map((tab, index) => (
        <button
          className="qq-btn"
          type="button"
          key={tab}
          role="tab"
          id={`${tabsId}-${tab}`}
          aria-controls={`${tabsId}-panel-${tab}`}
          aria-selected={pane === tab}
          tabIndex={pane === tab ? 0 : -1}
          onClick={() => changePane(tab)}
          onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key))
              return;
            event.preventDefault();
            const next =
              event.key === "Home" ? 0 : event.key === "End" ? 1 : 1 - index;
            const buttons =
              event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                "button",
              );
            buttons?.[next]?.focus();
            changePane(next === 0 ? "details" : "ask_ai");
          }}
        >
          {tab === "details" ? "详情" : "问 AI"}
        </button>
      ))}
    </div>
  ) : null;
  return createPortal(
    <QuoteSideDrawer
      railTarget={host ? (target?.railTarget ?? null) : null}
      inlineTarget={host ? target?.inlineTarget : parking}
      enabled={!!host && !!target?.quoteId}
      ownerKey={ownerKey}
      pane={pane}
      requestedOpen={view?.requestedOpen}
      requestId={view?.requestId}
      onPaneChange={changePane}
      onRequestClose={workspace.closeAskAi}
      returnFocus={
        target?.askAiEligible
          ? () =>
              workspace.status === "ready"
                ? (target?.returnFocus() ?? null)
                : null
          : undefined
      }
    >
      {(!narrow || !!target?.inlineTarget) && tabs}
      <div
        className="qq-chat-details"
        role={target?.askAiEligible ? "tabpanel" : undefined}
        id={`${tabsId}-panel-details`}
        aria-labelledby={
          target?.askAiEligible ? `${tabsId}-details` : undefined
        }
        hidden={pane !== "details"}
      >
        {target?.details}
      </div>
      <div
        className="qq-chat-ask"
        role={target?.askAiEligible ? "tabpanel" : undefined}
        id={`${tabsId}-panel-ask_ai`}
        aria-labelledby={target?.askAiEligible ? `${tabsId}-ask_ai` : undefined}
        hidden={pane !== "ask_ai" || workspace.status !== "ready"}
      >
        {target?.askAiEligible &&
          target.quoteId &&
          mountedPanels.current.has(panelKey) && (
            <Suspense fallback={<p role="status">正在加载报价对话…</p>}>
              <QuoteChatSidePanel
                key={panelKey}
                quoteId={target.quoteId}
                actionOwner={actionOwner}
                ownsView={ownsView}
                sessionId={
                  view?.sessionId ??
                  `quote-side:${workspace.identity?.userId}:${target.quoteId}`
                }
                active={showingAsk && workspace.status === "ready"}
              />
            </Suspense>
          )}
      </div>
      {pane === "ask_ai" && workspace.status !== "ready" && (
        <p role="status">报价身份待核对，对话内容暂不可见。</p>
      )}
    </QuoteSideDrawer>,
    root,
  );
}
